"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { loginUser } from "@/api-service/auth";
import { toast } from "sonner";
import { useAuth } from "@/packages/context/AuthContext";
import { usePathname, useSearchParams } from "next/navigation";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import NextImage from "../NextImage";

const signInSchema = z.object({
  email: z
    .string()
    .min(1, "Email or Phone number is required")
    .refine(
      (val) =>
        z.string().email().safeParse(val).success ||
        /^\+?[0-9\s-]{7,20}$/.test(val.trim()),
      { message: "Please enter a valid Email address or Phone number" }
    ),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type SignInData = z.infer<typeof signInSchema>;

interface SignInFormV2Props {
  onSuccess?: () => void;
}

export default function SignInFormV2({ onSuccess }: SignInFormV2Props) {
  const searchParams = useSearchParams();
  const emailParam = searchParams?.get("email") || "";

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: emailParam,
      password: "",
    },
  });

  const { login } = useAuth();
  const { fastPush } = useFastRouter();
  const pathname = usePathname();

  const onSubmit = async (data: SignInData) => {
    try {
      setLoading(true);
      const response = await loginUser(data.email, data.password);

      const redirect = searchParams?.get("redirect");
      let target = "/";
      if (redirect) {
        target = redirect;
      } else if (pathname === "/checkout") {
        const search = searchParams ? searchParams.toString() : "";
        const query = search ? `?${search}` : "";
        target = `/checkout${query}`;
      }

      login(response?.data, target);
      toast.success("Login Successful");

      if (onSuccess) onSuccess();
    } catch (error: unknown) {
      const errRecord = (error as Record<string, unknown>)?.response as
        | Record<string, unknown>
        | undefined;
      const errData = errRecord?.data as Record<string, unknown> | undefined;
      const message =
        (errData?.message as string) || "Login failed. Please try again.";
      console.log("Login error:", error);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    fastPush("/forgot-password");
  };

  return (
    <div className="min-h-[85vh] w-full flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 bg-[#2A1E1E]/90">
      <div className="w-full max-w-5xl bg-white rounded-[32px] shadow-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-12 min-h-[580px] border border-slate-100">
        
        {/* Left Column: Login Form */}
        <div className="lg:col-span-6 p-8 sm:p-10 lg:p-12 flex flex-col justify-between bg-white">
          <div>
            {/* Store Logo */}
            <div className="mb-6 cursor-pointer" onClick={() => fastPush("/")}>
              <NextImage
                src="/assets/site/images/dnplogo.png"
                alt="Muthu Metals Logo"
                width={140}
                height={45}
                className="object-contain"
                priority
              />
            </div>

            {/* Subtitle & Heading */}
            <p className="text-xs sm:text-sm font-medium text-slate-400 tracking-wide mb-1">
              Welcome back !!!
            </p>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-8">
              Sign in
            </h1>

            {/* Form Inputs */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Email / Mobile Field */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-2">
                  Email or Mobile Number
                </label>
                <input
                  type="text"
                  placeholder="Enter your email or mobile"
                  {...register("email")}
                  className={`w-full bg-[var(--brand-surface-light)] border ${
                    errors.email ? "border-rose-500" : "border-slate-200/80"
                  } focus:border-[var(--brand-accent)] focus:bg-white rounded-xl px-4 py-3.5 text-sm text-slate-800 placeholder-slate-400 transition-all outline-none`}
                />
                {errors.email && (
                  <p className="text-xs text-rose-500 mt-1 font-medium">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-xs font-semibold text-slate-500">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-xs text-slate-400 hover:text-[var(--brand-accent)] transition-colors font-medium"
                  >
                    Forgot Password ?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••••••"
                    {...register("password")}
                    className={`w-full bg-[var(--brand-surface-light)] border ${
                      errors.password ? "border-rose-500" : "border-slate-200/80"
                    } focus:border-[var(--brand-accent)] focus:bg-white rounded-xl px-4 py-3.5 pr-11 text-sm text-slate-800 placeholder-slate-400 transition-all outline-none`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-rose-500 mt-1 font-medium">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* Action Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white font-bold py-3.5 px-8 rounded-full shadow-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60 cursor-pointer text-xs uppercase tracking-wider min-w-[150px]"
                >
                  <span>{loading ? "SINGING IN..." : "SING IN"}</span>
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </div>

          {/* Sign Up Link */}
          <div className="mt-8 text-xs font-medium text-slate-500">
            I don&apos;t have an account ?{" "}
            <button
              type="button"
              onClick={() => fastPush("/sign-up")}
              className="text-[var(--brand-accent)] font-bold hover:underline cursor-pointer"
            >
              Sign up
            </button>
          </div>
        </div>

        {/* Right Column: E-commerce Shopping Illustration */}
        <div className="hidden lg:flex lg:col-span-6 bg-gradient-to-br from-[#FFF3EC] via-[#FDE8DC] to-[#FCDED0] relative items-center justify-center p-8 overflow-hidden">
          {/* Decorative Grid Patterns */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#E86A4510_1px,transparent_1px),linear-gradient(to_bottom,#E86A4510_1px,transparent_1px)] bg-[size:24px_24px]" />
          
          <div className="relative z-10 w-full max-w-md flex flex-col items-center justify-center">
            {/* Custom E-Commerce Vector Illustration */}
            <svg
              viewBox="0 0 500 450"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-auto drop-shadow-xl"
            >
              {/* Background Shapes */}
              <circle cx="250" cy="200" r="140" fill="#FFE3D6" opacity="0.8" />
              <path
                d="M100 380 Q 250 420 400 380"
                stroke="#E86A45"
                strokeWidth="2"
                strokeDasharray="4 4"
                opacity="0.4"
              />

              {/* Shopping Cart */}
              <g transform="translate(60, 160)">
                {/* Cart Wheels */}
                <circle cx="80" cy="180" r="12" fill="#2D2B3A" />
                <circle cx="160" cy="180" r="12" fill="#2D2B3A" />
                <circle cx="80" cy="180" r="5" fill="#FFFFFF" />
                <circle cx="160" cy="180" r="5" fill="#FFFFFF" />

                {/* Cart Wireframe Body */}
                <path
                  d="M30 30 L60 30 L90 140 L180 140 L210 50 L55 50"
                  stroke="#2D2B3A"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                <path
                  d="M72 80 H200 M80 110 H190 M110 50 V140 M150 50 V140"
                  stroke="#2D2B3A"
                  strokeWidth="2"
                  strokeOpacity="0.3"
                />

                {/* Items Inside Cart */}
                {/* Cyan Box */}
                <rect x="70" y="85" width="45" height="45" rx="4" fill="#00E5D1" />
                {/* Blue Box */}
                <rect x="125" y="95" width="45" height="35" rx="4" fill="#2B52F5" />
                {/* Orange Circle Item */}
                <circle cx="140" cy="70" r="30" fill="#E86A45" />
              </g>

              {/* Shopping Character Person */}
              <g transform="translate(260, 140)">
                {/* Shadow */}
                <ellipse cx="60" cy="245" rx="45" ry="8" fill="#2D2B3A" opacity="0.15" />

                {/* Legs */}
                <path
                  d="M45 130 L40 240 L55 240 L55 130 Z"
                  fill="#2D2B3A"
                />
                <path
                  d="M65 130 L70 240 L85 240 L75 130 Z"
                  fill="#1F1D2B"
                />

                {/* Shoes */}
                <path d="M30 240 H55 V246 H30 Z" fill="#2D2B3A" />
                <path d="M70 240 H95 V246 H70 Z" fill="#2D2B3A" />

                {/* Torso / Orange Jacket */}
                <path
                  d="M30 45 C30 45 40 30 60 30 C80 30 90 45 90 45 L95 130 H25 L30 45 Z"
                  fill="#E86A45"
                />

                {/* Inner Shirt */}
                <path d="M50 32 L60 80 L70 32 Z" fill="#FFFFFF" />

                {/* Arms & Holding Clipboard/Package */}
                <path
                  d="M25 48 L10 90 L35 90 L40 60 Z"
                  fill="#D85934"
                />
                <rect
                  x="30"
                  y="75"
                  width="40"
                  height="50"
                  rx="4"
                  fill="#F5C4A3"
                  transform="rotate(-5 50 100)"
                />

                {/* Head & Hair */}
                <circle cx="60" cy="18" r="14" fill="#F9D0B6" />
                {/* Dark Hair */}
                <path
                  d="M46 16 C46 5 54 2 64 2 C74 2 76 10 76 16 C70 12 65 18 60 14 C55 18 50 12 46 16 Z"
                  fill="#2D2B3A"
                />
              </g>

              {/* Decorative Floating Sparkles / Elements */}
              <circle cx="380" cy="100" r="6" fill="#E86A45" opacity="0.6" />
              <circle cx="120" cy="120" r="4" fill="#00E5D1" opacity="0.8" />
              <path
                d="M60 370 Q 70 340 90 360"
                stroke="#E86A45"
                strokeWidth="3"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </div>
        </div>

      </div>
    </div>
  );
}
